export const OCUPACAO_FILTER_FIELDS = {
  cidade: 'cidade',
  projeto: 'projeto',
  pop: 'pop',
  faixa_ocupacao: 'faixa_ocupacao',
};

export const normalizeText = (value, fallback = 'Não informado') => {
  const text = String(value ?? '').trim();
  return text ? text : fallback;
};

export const normalizeNumber = (value) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
};

export const normalizeCoordinate = (value) => {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  const coordinate = Number.parseFloat(value);
  return Number.isFinite(coordinate) ? coordinate : null;
};

export const getOccupancyRange = (percentual) => {
  const rate = normalizeNumber(percentual);

  if (rate > 80) {
    return 'Critica';
  }

  if (rate > 50) {
    return 'Media';
  }

  return 'Baixa';
};

export const getOccupancyRangeLabel = (value) => {
  switch (value) {
    case 'Media':
      return 'Média';
    case 'Critica':
      return 'Crítica';
    case 'Baixa':
      return 'Baixa';
    default:
      return normalizeText(value);
  }
};

export const buildOccupancyFilterLabel = (field, value) => {
  const normalizedValue = normalizeText(value);

  switch (field) {
    case OCUPACAO_FILTER_FIELDS.cidade:
      return `Cidade: ${normalizedValue}`;
    case OCUPACAO_FILTER_FIELDS.projeto:
      return `Projeto: ${normalizedValue}`;
    case OCUPACAO_FILTER_FIELDS.pop:
      return `POP: ${normalizedValue}`;
    case OCUPACAO_FILTER_FIELDS.faixa_ocupacao:
      return `Faixa: ${getOccupancyRangeLabel(normalizedValue)}`;
    default:
      return normalizedValue;
  }
};

export const sameOccupancyFilter = (left, right) => (
  left?.field === right?.field
  && String(left?.value ?? '') === String(right?.value ?? '')
);

export const normalizeCtoRow = (row, index) => {
  const capacidade = normalizeNumber(row?.capacidade);
  const portasOcupadas = normalizeNumber(row?.portas_ocupadas);
  const portasVagas = normalizeNumber(row?.portas_vagas);
  const percentualOcupacao = normalizeNumber(row?.percentual_ocupacao);

  return {
    id_cto: row?.id_cto ?? `cto-${index}`,
    caixa: normalizeText(row?.caixa),
    capacidade,
    latitude: normalizeCoordinate(row?.latitude),
    longitude: normalizeCoordinate(row?.longitude),
    projeto: normalizeText(row?.projeto),
    pop: normalizeText(row?.pop),
    id_cidade: row?.id_cidade ?? null,
    cidade: normalizeText(row?.cidade),
    portas_ocupadas: portasOcupadas,
    portas_vagas: portasVagas,
    percentual_ocupacao: percentualOcupacao,
    faixa_ocupacao: getOccupancyRange(percentualOcupacao),
  };
};

export const hasValidCoordinates = (row) => (
  Number.isFinite(row?.latitude) && Number.isFinite(row?.longitude)
);

export const applyOcupacaoFilters = (rows, filters) => {
  if (!Array.isArray(filters) || filters.length === 0) {
    return rows;
  }

  const groupedFilters = filters.reduce((acc, filter) => {
    if (!filter?.field) {
      return acc;
    }

    if (!acc[filter.field]) {
      acc[filter.field] = [];
    }

    acc[filter.field].push(filter.value);
    return acc;
  }, {});

  return rows.filter((row) => (
    Object.entries(groupedFilters).every(([field, values]) => {
      const rowValue = field === OCUPACAO_FILTER_FIELDS.faixa_ocupacao
        ? getOccupancyRange(row?.percentual_ocupacao)
        : normalizeText(row?.[field]);

      return values.some((value) => String(rowValue) === String(normalizeText(value)));
    })
  ));
};

export const aggregateBy = (rows, key) => {
  const map = new Map();

  rows.forEach((row) => {
    const label = normalizeText(row?.[key]);
    const current = map.get(label) || {
      label,
      ocupadas: 0,
      vagas: 0,
      capacidade: 0,
      count: 0,
    };

    current.ocupadas += normalizeNumber(row?.portas_ocupadas);
    current.vagas += normalizeNumber(row?.portas_vagas);
    current.capacidade += normalizeNumber(row?.capacidade);
    current.count += 1;
    map.set(label, current);
  });

  return Array.from(map.values())
    .map((item) => ({
      ...item,
      total: item.ocupadas + item.vagas,
      ocupacao: item.capacidade > 0
        ? (item.ocupadas / item.capacidade) * 100
        : (item.ocupadas + item.vagas > 0 ? (item.ocupadas / (item.ocupadas + item.vagas)) * 100 : 0),
    }))
    .sort((a, b) => b.ocupadas - a.ocupadas || b.ocupacao - a.ocupacao || a.label.localeCompare(b.label, 'pt-BR'));
};

export const buildOcupacaoDashboardSnapshot = (rows) => {
  const grouped = {
    cidade: new Map(),
    projeto: new Map(),
    pop: new Map(),
    caixa: new Map(),
  };
  const faixaCounters = { Baixa: 0, Media: 0, Critica: 0 };

  let quantidadeCtos = 0;
  let portasOcupadas = 0;
  let portasVagas = 0;
  let totalPortas = 0;

  const mergeGroupMetadata = (current, row) => {
    ['cidade', 'projeto', 'pop'].forEach((field) => {
      const currentValue = normalizeText(current[field]);
      const rowValue = normalizeText(row?.[field]);

      if (!currentValue) {
        current[field] = rowValue;
      } else if (currentValue !== rowValue) {
        current[field] = 'Múltiplos';
      }
    });

    return current;
  };

  rows.forEach((row) => {
    quantidadeCtos += 1;
    portasOcupadas += normalizeNumber(row?.portas_ocupadas);
    portasVagas += normalizeNumber(row?.portas_vagas);
    totalPortas += normalizeNumber(row?.capacidade);

    const faixa = row?.faixa_ocupacao || getOccupancyRange(row?.percentual_ocupacao);
    if (faixaCounters[faixa] !== undefined) {
      faixaCounters[faixa] += 1;
    }

    ['cidade', 'projeto', 'pop', 'caixa'].forEach((key) => {
      const label = normalizeText(row?.[key]);
      const current = grouped[key].get(label) || {
        label,
        ocupadas: 0,
        vagas: 0,
        capacidade: 0,
        count: 0,
        cidade: normalizeText(row?.cidade),
        projeto: normalizeText(row?.projeto),
        pop: normalizeText(row?.pop),
      };

      current.ocupadas += normalizeNumber(row?.portas_ocupadas);
      current.vagas += normalizeNumber(row?.portas_vagas);
      current.capacidade += normalizeNumber(row?.capacidade);
      current.count += 1;
      mergeGroupMetadata(current, row);
      grouped[key].set(label, current);
    });
  });

  const toSortedArray = (map) => Array.from(map.values())
    .map((item) => ({
      ...item,
      total: item.ocupadas + item.vagas,
      ocupacao: item.capacidade > 0
        ? (item.ocupadas / item.capacidade) * 100
        : (item.ocupadas + item.vagas > 0 ? (item.ocupadas / (item.ocupadas + item.vagas)) * 100 : 0),
    }))
    .sort((a, b) => b.ocupadas - a.ocupadas || b.ocupacao - a.ocupacao || a.label.localeCompare(b.label, 'pt-BR'));

  const groupedRows = {
    cidade: toSortedArray(grouped.cidade),
    projeto: toSortedArray(grouped.projeto),
    pop: toSortedArray(grouped.pop),
    caixa: toSortedArray(grouped.caixa),
  };

  return {
    summary: {
      quantidadeCtos,
      portasOcupadas,
      portasVagas,
      totalPortas,
      ocupacaoMedia: totalPortas > 0 ? (portasOcupadas / totalPortas) * 100 : 0,
    },
    groupedRows,
    faixaSummary: Object.entries(faixaCounters)
      .map(([key, count]) => ({
        key,
        count,
        percent: quantidadeCtos > 0 ? (count / quantidadeCtos) * 100 : 0,
      }))
      .filter((item) => item.count > 0),
  };
};
